// frontend/modules/document-type/views/DocumentTypeDetailView.tsx
import React from 'react';
import { Typography, Button, Space, Spin, Alert, Card, Descriptions, Tag, Divider } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetDocumentType } from '@/shared/api/client/document-type-controller';
import { useDocumentTypes } from '../hooks/useDocumentTypes';

const { Title, Text } = Typography;

export const DocumentTypeDetailView: React.FC = () => {
    const navigate = useNavigate();
    const { code } = useParams<{ code: string }>();
    const { deleteDocumentType } = useDocumentTypes();

    const getErrorMessage = (error: unknown, fallback: string) => {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        if (typeof error === 'object' && error !== null && 'message' in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }

        return fallback;
    };

    const { data, isLoading, error } = useGetDocumentType(code!);

    const handleEdit = () => {
        navigate(`/admin/document-types/edit/${code}`);
    };

    const handleDelete = async () => {
        if (!code) return;
        
        try {
            await deleteDocumentType.mutateAsync({ code });
            navigate('/admin/document-types/management');
        } catch (error) {
            console.error('Failed to delete document type:', error);
        }
    };

    const formatFileSize = (sizeKb?: number) => {
        if (!sizeKb) return 'Not specified';
        if (sizeKb >= 1024) {
            return `${(sizeKb / 1024).toFixed(1)} MB`;
        }
        return `${sizeKb} KB`;
    };

    const documentType = data;

    const renderFileTypes = () => {
        if (!documentType?.allowedFileTypes || documentType.allowedFileTypes.length === 0) {
            return <Text type="secondary">All file types allowed</Text>;
        }

        return (
            <Space wrap>
                {documentType.allowedFileTypes.map(type => (
                    <Tag key={type}>
                        .{type}
                    </Tag>
                ))}
            </Space>
        );
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message="Error Loading Document Type"
                    description={getErrorMessage(error, 'Failed to load document type')}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!documentType) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message="Document Type Not Found"
                    description={`Document type with code "${code}" was not found.`}
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <Space>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/admin/document-types/management')}
                    >
                        Back to Document Types
                    </Button>
                </Space>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {documentType.name}
                        </Title>
                        <Text type="secondary" code style={{ fontSize: 14 }}>
                            {documentType.code}
                        </Text>
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                        >
                            Edit
                        </Button>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={handleDelete}
                            loading={deleteDocumentType.isPending}
                        >
                            Delete
                        </Button>
                    </Space>
                </div>
            </div>

            <Card>
                <Descriptions title="Document Type Details" bordered column={2}>
                    <Descriptions.Item label="Code" span={1}>
                        <Text code>{documentType.code}</Text>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Name" span={1}>
                        <Text strong>{documentType.name}</Text>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Status" span={1}>
                        {documentType.isActive ? (
                            <Tag color="green">Active</Tag>
                        ) : (
                            <Tag color="red">Inactive</Tag>
                        )}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Requirement" span={1}>
                        {documentType.isRequired ? (
                            <Tag color="orange">Required</Tag>
                        ) : (
                            <Tag color="blue">Optional</Tag>
                        )}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Display Order" span={1}>
                        {documentType.displayOrder}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Description" span={2}>
                        {documentType.description || <Text type="secondary">No description provided</Text>}
                    </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Descriptions title="File Constraints" bordered column={2}>
                    <Descriptions.Item label="Minimum Size" span={1}>
                        {formatFileSize(documentType.minUploadSizeKb)}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Maximum Size" span={1}>
                        {formatFileSize(documentType.maxUploadSizeKb)}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Allowed File Types" span={2}>
                        {renderFileTypes()}
                    </Descriptions.Item>
                </Descriptions>

                <Divider />

                <Descriptions title="Audit Information" bordered column={2}>
                    <Descriptions.Item label="Created By" span={1}>
                        {documentType.createdBy || <Text type="secondary">Unknown</Text>}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Created At" span={1}>
                        {documentType.createdAt ? new Date(documentType.createdAt).toLocaleString() : <Text type="secondary">Unknown</Text>}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Modified By" span={1}>
                        {documentType.modifiedBy || <Text type="secondary">Not modified</Text>}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Modified At" span={1}>
                        {documentType.modifiedAt ? new Date(documentType.modifiedAt).toLocaleString() : <Text type="secondary">Not modified</Text>}
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        </div>
    );
};
